import { StateCreator } from 'zustand';

export const PATIENT_WEB_LOCAL_DEV_SERVER_URL = 'http://localhost:3001';

/**
 * Force one of the intake-quiz error paths on the next BE call so they can
 * be exercised from the running app. Read by `IntakeQuizPage` and gated on
 * `isDevToolsEnabled()` — no effect in production. Set via the dev-tools
 * sidebar's `IntakeQuizSection`. Not persisted (resets on refresh) so it
 * doesn't surprise the next session.
 *
 * - `boot`: next `POST /sessions` call short-circuits to the createSession
 *   onError path → silent redirect to scheduling + experiment exclusion.
 * - `transient`: next `POST /responses` call short-circuits to the
 *   transient onError path → inline retry notice, question slides back in.
 * - `hard`: next `POST /responses` call short-circuits to the hard onError
 *   path → "Your session expired" panel with a Start over button.
 */
export type IntakeQuizForceError = 'boot' | 'transient' | 'hard' | null;

interface DevToolsActions {
  resetDevTools: () => void;
  setPatientWebUrl: (newUrl: string) => void;
  setIntakeQuizForceError: (mode: IntakeQuizForceError) => void;
}

type DevToolsState = typeof initialState;

export type DevToolsSlice = {
  devTools: DevToolsState & DevToolsActions;
};

// eslint-disable-next-line regex/invalid-error
const deployedPatientWebUrl = import.meta.env.VITE_DOCTOR_VISIT_URL;

const initialState = {
  isLocalPatientWeb: false,
  isDeployedPatientWeb: true,
  patientWebUrl: deployedPatientWebUrl,
  intakeQuizForceError: null as IntakeQuizForceError,
};

export const createDevToolsSlice: StateCreator<
  DevToolsSlice,
  [['zustand/devtools', never]]
> = (set, _get) => ({
  devTools: {
    ...initialState,
    resetDevTools: () => {
      set(
        (state) => {
          return {
            devTools: {
              ...state.devTools,
              ...initialState,
            },
          };
        },
        false,
        'DEV_TOOLS/RESET',
      );
    },
    setPatientWebUrl(newUrl) {
      set(
        (state) => {
          return {
            devTools: {
              ...state.devTools,
              isLocalPatientWeb: newUrl === PATIENT_WEB_LOCAL_DEV_SERVER_URL,
              isDeployedPatientWeb: newUrl === deployedPatientWebUrl,
              patientWebUrl: newUrl,
            },
          };
        },
        false,
        'DEV_TOOLS/SET_PATIENT_WEB_URL',
      );
    },
    setIntakeQuizForceError(mode) {
      set(
        (state) => ({
          devTools: { ...state.devTools, intakeQuizForceError: mode },
        }),
        false,
        'DEV_TOOLS/SET_INTAKE_QUIZ_FORCE_ERROR',
      );
    },
  },
});
